import { Alert, Divider } from "@mui/material";
import {
    changeContributionStateSelector,
    hasQuestionnaireSelector,
    pensionContributionChangeSelector,
    questionnaireSubmitDateSelector,
    useAppSelector,
} from "@repo/logic";
import DOMPurify from "dompurify";
import { FC, useEffect, useMemo } from "react";
import { NumberToCZK } from "../../helpers";
import { useTypedTranslation } from "../../hooks/useTypedTranslation";
import { Card } from "../Cards";
import { DefaultStepProps } from "../Stepper/CustomStepper";
import { BodyTextM } from "../Typography";
import * as Styled from "./ChangeContributionOverviewPage.styled";

type ChangeContributionOverviewPageProps = DefaultStepProps;

export const ChangeContributionOverviewPage: FC<ChangeContributionOverviewPageProps> = ({
    setModalTitle,
    setNextButtonDisabled,
    setContinueCondition,
}) => {
    const { t } = useTypedTranslation();
    const reduxState = useAppSelector(changeContributionStateSelector);
    const change = useAppSelector(pensionContributionChangeSelector);
    const hasQuestionnaire = useAppSelector(hasQuestionnaireSelector);
    const questionnaireSubmitDate = useAppSelector(questionnaireSubmitDateSelector);

    const sanitizedHtmlAlert = useMemo(() => {
        return DOMPurify.sanitize(t("employerContributeDialog.pensionQuestionnaireNote"), {
            ALLOWED_TAGS: ["strong"],
        });
    }, [t]);

    // Change title of modal - in case user goes back to this step
    useEffect(() => {
        setModalTitle?.(t("investmentStrategy.changePensionContribution.stepperTitle"));
    }, [setModalTitle, t]);

    const shouldWaitForQuestionnaireDecision =
        change.contributionChanged && questionnaireSubmitDate.status === "notLoaded";

    // When client changes contribution and has a questionnaire, disable next button until questionnaire decision is received.
    useEffect(() => {
        setNextButtonDisabled(shouldWaitForQuestionnaireDecision);
        setContinueCondition?.(() => !shouldWaitForQuestionnaireDecision);

        return () => {
            setNextButtonDisabled(false);
            setContinueCondition?.(() => true);
        };
    }, [setNextButtonDisabled, setContinueCondition, shouldWaitForQuestionnaireDecision]);

    return (
        <Styled.MainWrapper>
            <Styled.PageHeading>{t("global.changesOverview")}</Styled.PageHeading>
            <Card>
                <Styled.CardInnerStack divider={<Divider />}>
                    <Styled.StackEqualCols>
                        <Styled.StackColItem>
                            <Styled.BoldBodyTextM>{t("employerContributeDialog.newContribution")}</Styled.BoldBodyTextM>
                        </Styled.StackColItem>
                        <Styled.StackColItem>
                            <BodyTextM>{NumberToCZK(reduxState?.newMonthlyContribution ?? 0)}</BodyTextM>
                        </Styled.StackColItem>
                    </Styled.StackEqualCols>

                    <Styled.StackEqualCols>
                        <Styled.StackColItem>
                            <Styled.BoldBodyTextM>
                                {t("employerContributeDialog.employerContributes")}
                            </Styled.BoldBodyTextM>
                        </Styled.StackColItem>
                        <Styled.StackColItem>
                            <BodyTextM>
                                {reduxState?.newEmployerContribute ? t("global.yes") : t("global.no")}
                            </BodyTextM>
                        </Styled.StackColItem>
                    </Styled.StackEqualCols>
                </Styled.CardInnerStack>
            </Card>

            {change.contributionChanged && hasQuestionnaire && (
                <Alert severity="info">
                    <BodyTextM dangerouslySetInnerHTML={{ __html: sanitizedHtmlAlert }}></BodyTextM>
                </Alert>
            )}
        </Styled.MainWrapper>
    );
};
